import React from 'react';
import { isNullOrUndefined, isNullOrUndefinedOrEmpty, guid, utc } from 'rewire-common';
import { observable } from 'rewire-core';
import {
  Form,
  IFormContext,
  IEditorField,
  editor,
  arraySearch,
  EditorType,
  IFieldTypes,
  IActionMenuItem,
  IFieldDefns,
} from 'rewire-ui';
import { cloneValue } from 'rewire-grid';
import { FilterComparative, IUdf, FieldFilter, UdfType, lookupKeyFromUdfDocumentType } from '../DataTypes';
import isEmpty from '../utility/isEmpty';
import { boolSearcher } from '../utility/Searchers';
import DataQueries from '../graphql/Queries';
import { createUdfSearch } from '../utility/Searchers';
import { SvgIconProps } from '@material-ui/core/SvgIcon';
import { objectValues } from './Object';
import { FilterSpecialValueKey, ISchedulingPropertyMapField } from '../schedule/ScheduleRuleDialogModel';
import { ExpressionFilterFn, FilterFn } from '../schedule/AutoScheduleQueryMapper';
import { Q } from './q';

function customUdfRefernceQuery(
  udf: IUdf,
  addIncludeFilter: FilterFn,
  _: ExpressionFilterFn,
  addExcludeFilter: FilterFn,
  fieldFilter: FieldFilter,
): FieldFilter {
  const field = fieldFilter.field;
  if (!field) return fieldFilter;
  const refIndex = field.indexOf(':r') ?? -1;
  if (refIndex === -1) return fieldFilter;

  const nestedField = field.substring(0, refIndex + 2);
  let eqValue = fieldFilter.eq ?? fieldFilter.in ?? fieldFilter.nin ?? fieldFilter.ne;
  if (!eqValue) return fieldFilter;

  if ((eqValue?.id ?? eqValue?.[0]?.id) === FilterSpecialValueKey.SelectedTimecardsOccupation) {
    eqValue = '$context.occupationId';
  }

  const value = Q<any>().bool((b) =>
    b.must((m) => [
      m.nested(nestedField, (x) =>
        x.bool((b) =>
          b.filter((f) => {
            const result = [];
            if (Array.isArray(eqValue)) {
              result.push(f.terms(field, eqValue));
            } else {
              result.push(f.term(field, eqValue));
            }
            if (udf.hasEffective) {
              result.push(f.range(`${nestedField}.start`, { lte: '$context.earnedDate' }));
              result.push(f.range(`${nestedField}.end`, { gte: '$context.earnedDate' }));
            }
            return result;
          }),
        ),
      ),
    ]),
  );

  if (fieldFilter.nin || fieldFilter.ne) {
    addExcludeFilter({
      field: field.substring(0, refIndex),
      raw: {
        value,
      },
    });
  } else {
    addIncludeFilter({
      field: field.substring(0, refIndex),
      raw: {
        value,
      },
    });
  }
  return fieldFilter;
}

export function getPropertyMapFieldsFromUdfs(
  udfs: IUdf[],
  udfPropertyMapFieldEnhancer?: (propertyMapField: IPropertyMapField, udf: IUdf) => void,
) {
  const propertyMapFields: IPropertyMapField[] = [];
  for (const udf of udfs) {
    const udfLookupKey = lookupKeyFromUdfDocumentType(udf.documentType) as string;
    const udfField = `${udfLookupKey}.fields.${udf.name}${udf.type === 'reference' ? '.id' : ''}`;
    const udfPropertyMapField = {
      field: udfField,
      displayName: udf.displayName,
      valueType: udf.type,
      udf: udf,
      customFilter: udf.type === 'reference' ? customUdfRefernceQuery.bind(null, udf) : undefined,
    } as any;
    switch (udf.type) {
      case 'date':
        udfPropertyMapField['editorType'] = 'date';
        break;
      case 'float':
        udfPropertyMapField['editorType'] = 'number';
        udfPropertyMapField['editorOptions'] = { decimals: 4 };
        break;
      case 'integer':
        udfPropertyMapField['editorType'] = 'number';
        udfPropertyMapField['editorOptions'] = { decimals: 0 };
        break;
      case 'boolean':
        udfPropertyMapField['editorType'] = 'select';
        udfPropertyMapField['editorOptions'] = { ...boolSearcher };
        break;
      case 'text': {
        if (udf.allowableValues) {
          const textSearcher = createUdfSearch(udf, (v) => v.name);
          udfPropertyMapField['editorType'] = 'auto-complete';
          udfPropertyMapField['editorOptions'] = { ...textSearcher };
        } else {
          udfPropertyMapField['editorType'] = 'text';
          udfPropertyMapField['editorOptions'] = undefined;
        }
        break;
      }
      case 'reference': {
        const refSearcher = createUdfSearch(udf);
        udfPropertyMapField['editorType'] = 'auto-complete';
        udfPropertyMapField['editorOptions'] = { ...refSearcher };
        break;
      }
    }

    udfPropertyMapFieldEnhancer?.(udfPropertyMapField, udf);
    propertyMapFields.push(udfPropertyMapField);
  }

  return propertyMapFields;
}

export function getComparativesForValueType(valueType: UdfType): FilterComparative[] {
  const comparatives: FilterComparative[] = [];

  switch (valueType) {
    case 'boolean':
      comparatives.push(FilterComparative.Equals);
      comparatives.push(FilterComparative.NotEquals);
      break;
    case 'text':
    case 'reference':
      comparatives.push(FilterComparative.Equals);
      comparatives.push(FilterComparative.NotEquals);
      comparatives.push(FilterComparative.In);
      comparatives.push(FilterComparative.NotIn);
      break;
    case 'float':
    case 'integer':
    case 'date':
      comparatives.push(FilterComparative.Equals);
      comparatives.push(FilterComparative.NotEquals);
      comparatives.push(FilterComparative.LessThan);
      comparatives.push(FilterComparative.LessThanOrEquals);
      comparatives.push(FilterComparative.GreaterThan);
      comparatives.push(FilterComparative.GreaterThanOrEquals);
      comparatives.push(FilterComparative.Between);
      comparatives.push(FilterComparative.NotBetween);
      break;
  }

  return comparatives;
}

export interface ICustomFilterRowOption {
  title: string;
  icon?: (props: SvgIconProps) => JSX.Element | React.ComponentElement<SvgIconProps, any> | null;
  onClick: (form: Form, rowFormFields: ICustomFilterRowFormFields) => void;
}

export type TCustomEditorComopnentFn = (
  editorType: EditorType,
  valueType: UdfType,
  editorOptions: any,
) => (props: any) => JSX.Element;

export interface IPropertyMapField {
  field: string;
  displayName: string;
  group: string;
  valueType: UdfType;
  editorType: EditorType;
  editorOptions?: ObjectType;
  customEditorComponentFn?: TCustomEditorComopnentFn;
  rowOptions?: ICustomFilterRowOption[];
  comparatives?: FilterComparative[];
  intersectsFieldNames?: { lowerField: string; upperField: string };
  mapToForm?: (fieldFilter: FieldFilter) => FieldFilter;
  mapToSave?: (fieldFilter: FieldFilter, value: any, comparative: FilterComparative) => FieldFilter;
  udf?: IUdf;
}

export type PropertyMap = Record<string, IPropertyMapField>;

export const _comparativeMap: Record<string, string> = {
  [FilterComparative.Equals]: '=',
  [FilterComparative.LessThan]: '<',
  [FilterComparative.LessThanOrEquals]: '<=',
  [FilterComparative.GreaterThan]: '>',
  [FilterComparative.GreaterThanOrEquals]: '>=',
  [FilterComparative.NotEquals]: '!=',
  [FilterComparative.In]: 'in',
  [FilterComparative.NotIn]: 'not in',
  // [FilterComparative.Match]: 'match',
  // [FilterComparative.StartsWith]: 'starts with',
  [FilterComparative.Between]: 'between',
  [FilterComparative.Contains]: 'contains',
  [FilterComparative.NotBetween]: 'not between',
  [FilterComparative.Intersects]: 'intersects',
  [FilterComparative.NotIntersects]: 'not intersects',
};

export function isArrayValueFilterComparative(filterComparative?: FilterComparative): boolean {
  return filterComparative === FilterComparative.In || filterComparative === FilterComparative.NotIn;
}

export function isMultiValueEditorType(editorType: EditorType) {
  return editorType === 'multiselect' || editorType === 'multiselectautocomplete';
}

export function isIntersectingFilterComparative(filterComparative?: FilterComparative): boolean {
  return filterComparative === FilterComparative.Intersects || filterComparative === FilterComparative.NotIntersects;
}

export function isDualValueComparative(filterComparative?: FilterComparative): boolean {
  return (
    filterComparative === FilterComparative.Between ||
    filterComparative === FilterComparative.NotBetween ||
    filterComparative === FilterComparative.Intersects ||
    filterComparative === FilterComparative.NotIntersects
  );
}

export const _multiValueEditorTypeMap: Record<string, EditorType> = {
  'auto-complete': 'multiselectautocomplete',
  select: 'multiselect',
  text: 'multiselectautocomplete',
};

export const _formFieldTypeMap: Record<EditorType, IFieldTypes> = {
  text: 'string',
  multitext: 'multistring',
  static: 'static',
  select: 'select',
  multiselect: 'multiselect',
  'auto-complete': 'reference',
  checked: 'boolean',
  switch: 'switch',
  date: 'date',
  time: 'time',
  password: 'password',
  email: 'email',
  phone: 'phone',
  number: 'number',
  avatar: 'avatar',
  color: 'color',
  mask: 'mask',
  custom: 'custom',
  multiselectautocomplete: 'multiselectautocomplete',
  none: 'static',
};

interface ICustomFilterPropertySearcher {
  map: any;
  search: any;
}

type CustomFilterPropertySearchers = Record<string, ICustomFilterPropertySearcher>;

export interface ICustomFilterRowFormValues {
  property?: IPropertyMapField;
  comparative?: FilterComparative;
  value1?: any;
  value2?: any;
}

export interface ICustomFilterRowFormField {
  editorField: IEditorField;
  columns: number;
}

export interface ICustomFilterRowFormFields {
  property: ICustomFilterRowFormField;
  comparative: ICustomFilterRowFormField;
  value1: ICustomFilterRowFormField;
  value2: ICustomFilterRowFormField;
}

export interface ICustomFilterRow {
  id: string;
  group: string;
  data: ICustomFilterRowFormFields;
  rowOptionsMenuItems?: IActionMenuItem[];
  reloadEditorsToggle: boolean;
  __prevComparative?: FilterComparative;
}

export interface ICustomFilterModelState {
  form: Form;
  filterRows: ICustomFilterRow[];
  propertyMapGroups: string[];
}

export class CustomFilterModel {
  state: ICustomFilterModelState;
  protected _propertyMap: PropertyMap;
  protected propertySearchers: CustomFilterPropertySearchers;
  protected comparativeSearcher: any;

  constructor(propertyMapFields?: IPropertyMapField[], form?: Form) {
    this.state = observable({
      form: form ?? Form.create((_ctx: IFormContext) => ({}), {}, { variant: 'outlined', initialValuesValidationMode: 'none' }),
      filterRows: [],
      propertyMapGroups: [],
    });

    this.generatePropertyMap(propertyMapFields);
    this.comparativeSearcher = arraySearch<FilterComparative>(Object.values(FilterComparative), (item?: FilterComparative) =>
      item ? _comparativeMap[item] : '',
    );
  }

  get propertyMap() {
    return this._propertyMap;
  }

  generatePropertyMap(propertyMapFields: IPropertyMapField[] = []) {
    this._propertyMap = propertyMapFields
      .slice()
      .sort(this.propertyMapFieldsSort)
      .reduce((map: PropertyMap, curr: IPropertyMapField) => {
        const mapField = Object.assign({}, curr);
        if (isNullOrUndefined(mapField.comparatives)) {
          mapField.comparatives = getComparativesForValueType(mapField.valueType);
        }
        map[mapField.field] = mapField;
        return map;
      }, {} as PropertyMap);

    this.generatePropertySearchers();

    // update filter rows to reflect new property map
    for (let i = this.filterRows.length - 1; i >= 0; i--) {
      const row = this.filterRows[i];
      const rowPropertyField = row.data.property.editorField;
      const rowPropertyMapField = this._propertyMap[rowPropertyField.value.field];
      if (!rowPropertyMapField) {
        // remove filter rows that have properties that no longer exist
        this.removeFilterRow(row.id);
      } else {
        rowPropertyField.Editor = (props: any) =>
          React.createElement(editor('auto-complete', this.propertySearchers[row.group]), {
            ...props,
            field: rowPropertyField,
            onValueChange: this.getFormOnValueChangeFn(rowPropertyField),
          });
      }
      this.reloadRowEditors(row);
    }
  }

  protected generatePropertySearchers() {
    if (!this.propertyMap) return;

    const propertyMapByGroup: Record<string, any> = {};
    for (const propertyFieldValue of objectValues(this.propertyMap)) {
      const group = propertyFieldValue.group;
      if (!propertyMapByGroup[group]) {
        propertyMapByGroup[group] = [];
      }
      propertyMapByGroup[group].push(propertyFieldValue);
    }

    this.propertySearchers = {};
    const propertyMapGroups = Object.keys(propertyMapByGroup);
    for (const group of propertyMapGroups) {
      const groupPropertyMap = propertyMapByGroup[group];
      this.propertySearchers[group] = arraySearch<IPropertyMapField>(
        Object.values(groupPropertyMap),
        (item?: IPropertyMapField) => item?.displayName || '',
      );
    }
    this.state.propertyMapGroups = propertyMapGroups;
  }

  protected propertyMapFieldsSort(pmf1: IPropertyMapField, pmf2: IPropertyMapField) {
    return pmf1.displayName.localeCompare(pmf2.displayName);
  }

  reset(resetForm: boolean = true) {
    if (resetForm) {
      this.form.reset();
    }
    this.removeAllFilterRows();
  }

  load = async (filters?: FieldFilter[]) => {
    this.reset();

    const validFilters = filters?.filter((f) => !!this._propertyMap[f.field]);
    if (validFilters?.length) {
      const rowPositions: Record<string, number> = {};

      await Promise.all(
        validFilters.map(async (fieldFilter: FieldFilter, idx: number) => {
          const rowId = guid();
          rowPositions[rowId] = idx;
          return this.addFilterRow(fieldFilter, rowId);
        }),
      );

      this.filterRows.sort((a, b) => rowPositions[a.id] - rowPositions[b.id]);
    }
  };

  get form(): Form {
    return this.state.form;
  }

  get filterRows(): ICustomFilterRow[] {
    return this.state.filterRows;
  }

  get propertyMapGroups() {
    return this.state.propertyMapGroups;
  }

  filterRowsByGroup(group: string) {
    return this.filterRows.filter((row) => row.group === group);
  }

  removeAllFilterRows() {
    const filterRowIds = this.filterRows.map((f) => f.id);
    const filterRowFieldNames = Object.values(this.form.fields)
      .map((f) => f.name)
      .filter((fieldName) => filterRowIds.some((id) => fieldName.includes(`-${id}`)));
    this.form.removeFields(filterRowFieldNames);
    this.filterRows.set([]);
  }

  removeFilterRow(id: string) {
    const index = this.filterRows.findIndex((r) => r.id === id);
    if (index !== -1) {
      this.filterRows.splice(index, 1);

      const filterRowFieldNames = Object.values(this.form.fields)
        .map((f) => f.name)
        .filter((f) => f.includes(`-${id}`));
      this.form.removeFields(filterRowFieldNames);
    }
  }

  async addGroupFilterRow(group: string) {
    await this._addFilterRow(group);
  }

  async addFilterRow(fieldFilter: FieldFilter, id: string) {
    const group = this.propertyMap[fieldFilter.field]?.group;
    await this._addFilterRow(group, fieldFilter, id);
  }

  protected async _addFilterRow(group: string, fieldFilter?: FieldFilter, id?: string) {
    const filterRow: ICustomFilterRow = await this.createFilterRow(group, fieldFilter, id);
    this.filterRows.push(filterRow);
  }

  protected async createFilterRow(group: string, fieldFilter?: FieldFilter, id?: string): Promise<ICustomFilterRow> {
    id = id ?? guid();
    const formFieldNamesMap = {
      property: `property-${id}`,
      comparative: `comparative-${id}`,
      value1: `value1-${id}`,
      value2: `value2-${id}`,
    };

    const formValues = await this.mapFieldFilterToFormFields(fieldFilter);

    const comparativeSearcher = Object.assign({}, this.comparativeSearcher);
    comparativeSearcher.search = async (searchText: string) => {
      const results = await this.comparativeSearcher.search(searchText);
      const propertyFieldValue: IPropertyMapField | undefined = this.form.field[formFieldNamesMap['property']]?.value;
      return results.filter(
        (r: FilterComparative) => isNullOrUndefined(propertyFieldValue) || propertyFieldValue.comparatives?.includes(r),
      );
    };

    let valueEditorType: EditorType = 'text';
    let valueValueType: UdfType = 'text';
    let valueEditorOptions: ObjectType | undefined;
    let valueCustomEditorComponentFn: TCustomEditorComopnentFn | undefined;

    this.form.addFields(
      (_) => {
        const fields: IFieldDefns = {};
        const propertyField = _.reference(this.propertySearchers[group]).label('Property').validators('required');
        fields[formFieldNamesMap['property']] = propertyField;
        const comparativeField = _.select(comparativeSearcher, { dynamic: true }).label('Comparative').validators('required');
        fields[formFieldNamesMap['comparative']] = comparativeField;

        if (!isNullOrUndefined(formValues.property) && !isNullOrUndefined(formValues.comparative)) {
          const valueFieldsEditorProperties = this.getValueFieldsEditorProperties(formValues.property, formValues.comparative);
          valueEditorType = valueFieldsEditorProperties.editorType;
          valueValueType = valueFieldsEditorProperties.valueType;
          valueEditorOptions = valueFieldsEditorProperties.editorOptions;
          valueCustomEditorComponentFn = valueFieldsEditorProperties.customEditorComponentFn;
        }

        const { value1Label, value2Label } = this.getValueFieldLabels(
          formValues.property?.displayName,
          isDualValueComparative(formValues.comparative),
        );

        const value1Field = this.getFormValueField(_, valueEditorType, value1Label, valueEditorOptions);
        fields[formFieldNamesMap['value1']] = value1Field;
        const value2Field = this.getFormValueField(_, valueEditorType, value2Label, valueEditorOptions);
        fields[formFieldNamesMap['value2']] = value2Field;

        return fields;
      },
      {
        ...this.form.toObject(),
        [formFieldNamesMap['property']]: formValues.property,
        [formFieldNamesMap['comparative']]: formValues.comparative,
        [formFieldNamesMap['value1']]: formValues.value1,
        [formFieldNamesMap['value2']]: formValues.value2,
      },
    );

    const propertyField = this.form.field[formFieldNamesMap['property']];
    const comparativeField = this.form.field[formFieldNamesMap['comparative']];
    const value1Field = this.form.field[formFieldNamesMap['value1']];
    const value2Field = this.form.field[formFieldNamesMap['value2']];

    if (valueCustomEditorComponentFn) {
      value1Field.Editor = this.createEditor(
        value1Field,
        valueEditorType,
        valueValueType,
        valueEditorOptions,
        valueCustomEditorComponentFn,
      );
      value2Field.Editor = this.createEditor(
        value2Field,
        valueEditorType,
        valueValueType,
        valueEditorOptions,
        valueCustomEditorComponentFn,
      );
    }

    const row: ICustomFilterRow = observable({
      id,
      group,
      data: {
        property: { editorField: propertyField, columns: 2 },
        comparative: { editorField: comparativeField, columns: 1 },
        value1: { editorField: value1Field, columns: 2 },
        value2: { editorField: value2Field, columns: 2 },
      },
      rowOptionsMenuItems: undefined,
      reloadEditorsToggle: false,
      __prevComparative: comparativeField.value,
    });

    this.setRowOptionsMenuItems(row, formValues.property);

    comparativeField.disabled = () => propertyField.value == null || propertyField.value.comparatives?.length === 0;
    value1Field.disabled = () => isNullOrUndefined(propertyField.value) || isNullOrUndefined(comparativeField.value);
    value2Field.disabled = () => isNullOrUndefined(propertyField.value) || !isDualValueComparative(comparativeField.value);

    value2Field.visible = isDualValueComparative(comparativeField.value);

    propertyField.onValueChange = this.handlePropertyChangeHandler(row);
    comparativeField.onValueChange = this.handleComparativeChangeHandler(row);

    return row;
  }

  getFormValueField(formContext: IFormContext, valueEditorType: EditorType, valueLabel: string, valueEditorOptions?: ObjectType) {
    const valueFieldType = _formFieldTypeMap[valueEditorType];
    const valueField = formContext[valueFieldType](valueEditorOptions).label(valueLabel).validators('required');

    // if (valueEditorType === 'email') {
    //   valueField.validators('email');
    // } else if (valueEditorType === 'phone') {
    //   const phoneLength = ((valueEditorOptions && valueEditorOptions.format) || defaultPhoneFormat).replace(new RegExp('[^#]', 'g'), '').length;
    //   const phoneRegEx  = new RegExp('^$|^[0-9]{' + phoneLength + '}$');
    //   valueField.validators(validator('regex', phoneRegEx));
    // }

    return valueField;
  }

  setRowOptionsMenuItems(row: ICustomFilterRow, propertyValue?: IPropertyMapField) {
    if (!isNullOrUndefined(propertyValue)) {
      const rowOptions = propertyValue?.rowOptions;
      row.rowOptionsMenuItems = rowOptions?.map(
        (rowOption) =>
          ({
            name: rowOption.title,
            title: rowOption.title,
            icon: rowOption.icon,
            closeOnClick: true,
            disabled: () => isNullOrUndefined(row.data.comparative.editorField.value),
            onClick: () => rowOption.onClick(this.form, row.data),
          }) as IActionMenuItem,
      );
    } else {
      row.rowOptionsMenuItems = undefined;
    }
  }

  handlePropertyChangeHandler = (row: ICustomFilterRow) => (form: Form, value: any) => {
    const value1Field = row.data.value1.editorField;
    const value2Field = row.data.value2.editorField;
    const comparativeField = row.data.comparative.editorField;

    form.setFieldValue(value1Field.name, undefined);
    form.setFieldValue(value2Field.name, undefined);

    if (!this.propertyCanHaveComparative(value, comparativeField.value)) {
      form.setFieldValue(comparativeField.name, undefined);
    }

    this.setRowOptionsMenuItems(row, value);
    this.updateValueFieldsEditors(row);
  };

  handleComparativeChangeHandler = (row: ICustomFilterRow) => (form: Form, value: any) => {
    const value1Field = row.data.value1.editorField;
    const value2Field = row.data.value2.editorField;

    if (isNullOrUndefined(value)) {
      form.setFieldValue(value1Field.name, undefined);
      form.setFieldValue(value2Field.name, undefined);
    } else {
      const prevComparative = row.__prevComparative;
      const prevComparativeIsArrayValue = isArrayValueFilterComparative(prevComparative);
      const currComparativeIsArrayValue = isArrayValueFilterComparative(value);

      if (prevComparativeIsArrayValue && !currComparativeIsArrayValue) {
        form.setFieldValue(value1Field.name, value1Field.value?.[0]);
      } else if (!prevComparativeIsArrayValue && currComparativeIsArrayValue && !isNullOrUndefined(value1Field.value)) {
        form.setFieldValue(value1Field.name, [value1Field.value]);
      }

      if (isDualValueComparative(value)) {
        if (isNullOrUndefined(value2Field.value)) {
          form.setFieldValue(value2Field.name, cloneValue(value1Field.value));
        }
      } else {
        form.setFieldValue(value2Field.name, undefined);
      }
    }

    this.updateValueFieldsEditors(row);
    row.__prevComparative = value;
  };

  protected getFormOnValueChangeFn(field: IEditorField) {
    return (v: any) => {
      const value = isNullOrUndefinedOrEmpty(v) ? undefined : v;
      this.form.setFieldValue(field.name, value);
    };
  }

  rowHasEnabledOptions(row: ICustomFilterRow): boolean {
    return !!row.rowOptionsMenuItems?.length && row.rowOptionsMenuItems.some((o) => !(o.disabled as () => boolean)());
  }

  protected getValueFieldsEditorProperties(propertyMapField: IPropertyMapField, comparative: FilterComparative) {
    const valueCustomEditorComponentFn = propertyMapField?.customEditorComponentFn;
    const propertyEditorType = propertyMapField?.editorType;
    const propertyValueType = propertyMapField?.valueType;
    const comparativeIsArrayValue = isArrayValueFilterComparative(comparative);
    const valueEditorType = comparativeIsArrayValue
      ? (_multiValueEditorTypeMap[propertyEditorType] ?? propertyEditorType)
      : propertyEditorType;
    let valueEditorOptions = propertyMapField.editorOptions;
    if (propertyEditorType === 'number') {
      valueEditorOptions = { thousandSeparator: true, ...valueEditorOptions };
    }
    if (comparativeIsArrayValue) {
      if (propertyEditorType === 'auto-complete') {
        valueEditorOptions = { chipLimit: 3, ...valueEditorOptions };
      } else if (propertyEditorType === 'text') {
        const textSearcher = arraySearch([], (v) => (Array.isArray(v) ? '' : v || ''));
        const inputAdd = (v: string) => v;
        valueEditorOptions = { ...textSearcher, inputAdd, chipLimit: 3, ...valueEditorOptions };
      }
    }

    return {
      editorType: valueEditorType,
      valueType: propertyValueType,
      editorOptions: valueEditorOptions,
      customEditorComponentFn: valueCustomEditorComponentFn,
    };
  }

  protected updateValueFieldsEditors(row: ICustomFilterRow) {
    const propertyFieldValue: IPropertyMapField | undefined = row.data.property.editorField.value;
    const comparativeFieldValue: FilterComparative | undefined = row.data.comparative.editorField.value;
    const value1EditorField = row.data.value1.editorField;
    const value2EditorField = row.data.value2.editorField;
    const _isDualValueComparative = isDualValueComparative(comparativeFieldValue);
    const { value1Label, value2Label } = this.getValueFieldLabels(propertyFieldValue?.displayName, _isDualValueComparative);

    value1EditorField.label = value1Label;
    value2EditorField.label = value2Label;
    value2EditorField.visible = _isDualValueComparative;

    let valueEditorType: EditorType = 'text';
    let valueValueType: UdfType = 'text';
    let valueEditorOptions: ObjectType | undefined;
    let valueCustomEditorComponentFn: TCustomEditorComopnentFn | undefined;

    if (!isNullOrUndefined(propertyFieldValue) && !isNullOrUndefined(comparativeFieldValue)) {
      // Set form editors for value1 and value2 to the correct types corresponding to the property and comparative chosen.
      const valueFieldsEditorProperties = this.getValueFieldsEditorProperties(propertyFieldValue, comparativeFieldValue);
      valueEditorType = valueFieldsEditorProperties.editorType;
      valueValueType = valueFieldsEditorProperties.valueType;
      valueEditorOptions = valueFieldsEditorProperties.editorOptions;
      valueCustomEditorComponentFn = valueFieldsEditorProperties.customEditorComponentFn;
    }

    value1EditorField.Editor = this.createEditor(
      value1EditorField,
      valueEditorType,
      valueValueType,
      valueEditorOptions,
      valueCustomEditorComponentFn,
    );
    value2EditorField.Editor = this.createEditor(
      value2EditorField,
      valueEditorType,
      valueValueType,
      valueEditorOptions,
      valueCustomEditorComponentFn,
    );

    this.reloadRowEditors(row);
  }

  protected createEditor(
    valueEditorField: IEditorField,
    valueEditorType: EditorType,
    valueValueType: UdfType,
    valueEditorOptions?: ObjectType,
    valueCustomEditorComponentFn?: TCustomEditorComopnentFn,
  ) {
    return (props: any) =>
      React.createElement(
        this.getUpdateValueFieldEditor(valueEditorType, valueValueType, valueEditorOptions, valueCustomEditorComponentFn),
        { ...props, field: valueEditorField, onValueChange: this.getFormOnValueChangeFn(valueEditorField) },
      );
  }

  protected getUpdateValueFieldEditor(
    valueEditorType: EditorType,
    valueValueType: UdfType,
    valueEditorOptions?: ObjectType,
    valueCustomEditorComponentFn?: TCustomEditorComopnentFn,
  ) {
    if (valueCustomEditorComponentFn) {
      return valueCustomEditorComponentFn(valueEditorType, valueValueType, valueEditorOptions);
    }
    return editor(valueEditorType, valueEditorOptions);
  }

  protected reloadRowEditors(row: ICustomFilterRow) {
    row.reloadEditorsToggle = !row.reloadEditorsToggle;
  }

  protected propertyCanHaveComparative(property?: IPropertyMapField, comparative?: FilterComparative) {
    if (isNullOrUndefined(property)) {
      return true;
    }
    if (isNullOrUndefined(comparative)) {
      return false;
    }

    return property.comparatives?.includes(comparative);
  }

  protected getAllFilterRowsFormFields(): IEditorField[] {
    const filterRowIds = this.filterRows.map((f) => f.id);
    return Object.values(this.form.fields).filter((field) => filterRowIds.some((id) => field.name.includes(`-${id}`)));
  }

  hasErrors() {
    return this.getAllFilterRowsFormFields().findIndex((field: IEditorField) => !!field.error) >= 0;
  }

  protected getValueFieldLabels(propertyDisplayName?: string, isDualValueComparative: boolean = false) {
    if (isNullOrUndefinedOrEmpty(propertyDisplayName)) {
      propertyDisplayName = 'Value';
    }

    const value1Label = isDualValueComparative ? `${propertyDisplayName} Lower` : propertyDisplayName;
    const value2Label = isDualValueComparative ? `${propertyDisplayName} Upper` : propertyDisplayName;

    return { value1Label, value2Label };
  }

  protected getFilterRowValues(row: ICustomFilterRow): ICustomFilterRowFormValues {
    return {
      property: row.data.property.editorField.value,
      comparative: row.data.comparative.editorField.value,
      value1: row.data.value1.editorField.value,
      value2: row.data.value2.editorField.value,
    };
  }

  protected getFilterRowMappedValues(row: ICustomFilterRow): ICustomFilterRowFormValues {
    const values = this.getFilterRowValues(row);
    const valueType = values.property?.valueType;
    const comparative = values.comparative;
    values.value1 = this.mapFormFieldValueToFieldFilterValue(values.value1, valueType, comparative);
    values.value2 = this.mapFormFieldValueToFieldFilterValue(values.value2, valueType, comparative);
    return values;
  }

  protected filterRowIsComplete(row: ICustomFilterRow) {
    const { property, comparative, value1, value2 } = this.getFilterRowValues(row);
    if ((property as ISchedulingPropertyMapField)?.customFilter) return true;

    return (
      !isNullOrUndefined(property) &&
      !isNullOrUndefined(comparative) &&
      !isNullOrUndefined(value1) &&
      (!isDualValueComparative(comparative) || !isNullOrUndefined(value2))
    );
  }

  protected async mapFieldFilterValueToFormFieldValue(value?: any, type?: UdfType, comparative?: FilterComparative): Promise<any> {
    if (type === 'reference') {
      const isArrayValueComparative = isArrayValueFilterComparative(comparative);
      const searchValue = isArrayValueComparative ? value : !isNullOrUndefined(value) ? [value] : [];
      const searchFilter = { field: 'id', in: searchValue };
      const objectValues = await DataQueries.getSearch('id name code', 'all', undefined, searchFilter, 100);
      return isArrayValueComparative ? objectValues : objectValues?.[0];
    }
    if (type === 'date') {
      return !isEmpty(value)
        ? isArrayValueFilterComparative(comparative)
          ? value.map((v: string) => utc(v))
          : utc(value)
        : undefined;
    }
    return value;
  }

  protected mapFormFieldValueToFieldFilterValue(value?: any, type?: UdfType, comparative?: FilterComparative): any {
    if (type === 'reference') {
      return isArrayValueFilterComparative(comparative) ? value?.map((v: ObjectType) => v.id) : value?.id;
    }
    if (type === 'date') {
      return !isEmpty(value)
        ? isArrayValueFilterComparative(comparative)
          ? value.map((v: ObjectType) => v.toDateString())
          : value.toDateString()
        : undefined;
    }
    return value;
  }

  protected async mapFieldFilterToFormFields(fieldFilter?: FieldFilter): Promise<ICustomFilterRowFormValues> {
    let property: IPropertyMapField | undefined;
    let comparative: FilterComparative | undefined;
    let value1: any;
    let value2: any;

    if (!isNullOrUndefined(fieldFilter)) {
      property = this._propertyMap[fieldFilter.field];

      if (property.mapToForm) {
        fieldFilter = property.mapToForm(fieldFilter);
      }

      const propertyComparatives = property.comparatives ?? [];

      for (const c of propertyComparatives) {
        if (!isNullOrUndefined(fieldFilter[c])) {
          const valueType = property?.valueType;
          comparative = c;
          const _isDualValueComparative = isDualValueComparative(comparative);
          [value1, value2] = await Promise.all([
            _isDualValueComparative
              ? this.mapFieldFilterValueToFormFieldValue(fieldFilter[c]!.lower, valueType, comparative)
              : this.mapFieldFilterValueToFormFieldValue(fieldFilter[c], valueType, comparative),
            _isDualValueComparative
              ? this.mapFieldFilterValueToFormFieldValue(fieldFilter[c]!.upper, valueType, comparative)
              : undefined,
          ]);
          break;
        }
      }
    }

    return {
      property: property,
      comparative: comparative,
      value1: value1,
      value2: value2,
    };
  }

  protected mapFormFieldsToFieldFilters(): FieldFilter[] {
    const fieldFilters: FieldFilter[] = [];
    for (const row of this.filterRows) {
      if (!this.filterRowIsComplete(row)) {
        continue;
      }

      const { property, comparative, value1, value2 } = this.getFilterRowMappedValues(row);

      let fieldFilter: FieldFilter = {
        field: property!.field,
      };

      if (isDualValueComparative(comparative)) {
        fieldFilter[comparative!] = { lower: value1!, upper: value2! };
        if (isIntersectingFilterComparative(comparative)) {
          const lowerField = property!.intersectsFieldNames?.lowerField;
          const upperField = property!.intersectsFieldNames?.upperField;
          if (!lowerField || !upperField) {
            continue;
          }
          fieldFilter[comparative!].lowerField = lowerField;
          fieldFilter[comparative!].upperField = upperField;
        }
      } else {
        fieldFilter[comparative!] = value1;
      }

      if (property!.mapToSave) {
        fieldFilter = property!.mapToSave(fieldFilter, fieldFilter[comparative!], comparative!);
      }

      fieldFilters.push(fieldFilter);
    }

    return fieldFilters;
  }

  mapToSave() {
    const customFilterInput = this.mapFormFieldsToFieldFilters();
    return customFilterInput;
  }
}

export default CustomFilterModel;
